import {
  Link,
  type LinkOptions,
  type LinkProps,
  type NavigateOptions,
  type RegisteredRouter,
} from '@tanstack/react-router'
import {
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type SortingState,
} from '@tanstack/react-table'
import { ChevronDown, ChevronRight, ChevronsUpDown, ChevronUp } from 'lucide-react'
import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

import Stagger from '@/components/motion/Stagger'
import { TOUCH_TARGET } from '@/components/ui/button-variants'
import EmptyState from '@/components/ui/EmptyState'
import RowCard from '@/components/ui/RowCard'
import { useRowCard, type RowCardContent } from '@/components/ui/row-card'
import Skeleton from '@/components/ui/Skeleton'
import { useRowNavigation } from '@/components/ui/use-row-navigation'
import { cn } from '@/lib/utils'

export interface DataTableColumnMeta {
  /**
   * Overrides the alignment `numeric` would pick. `center` is for a column whose cell draws
   * something symmetric about its own middle (a diverging bar's zero line): the head and the cell
   * share the column's padding, so the head's centre is the cell's centre.
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Price, kitta, percent — anything that has to line up digit over digit. Never wraps (see
   * `cellClasses`).
   *
   * Right-aligns via `text-align` on the `<th>` AND `<td>` (`cellClasses`),
   * which only positions INLINE content. A `cell` root that is a
   * block-level flex or grid box (`className="flex ..."` / `"grid ..."` —
   * NOT `inline-flex`) ignores it completely: the box already fills the
   * `<td>`'s width on its own (ordinary block sizing), and
   * `justify-content`'s default (`flex-start`) then packs its children at
   * the box's OWN start — the column's LEFT edge, whatever `text-align`
   * says. Sector flow's "Bought share" column lived this bug:
   * `SectorFlowTable`'s `BoughtShare` had a bare `flex` root and sat left of
   * its right-aligned header until it gained its own `justify-end`.
   * `inline-flex` has no such gap — its outer display is `inline`, so
   * `text-align` places the (shrink-to-fit) box exactly as it would a
   * `<span>`; see `Change`/`Badge`/`WeightBar`'s own roots. A `flex-col`
   * root aligns on `items-end` instead — the cross axis, not the main one —
   * see `CapitalShareCell` and `PublicIssuesScreen`'s price cell, both
   * already correct.
   *
   * Deliberately NOT handled here: reaching a block-level child would mean
   * flexing the `<td>` itself, which also flexes every plain-text numeric
   * cell (the overwhelming majority) and silently drops the free
   * `vertical-align: middle` a `<td>` gets by default — nothing in
   * `cellClasses` sets `align-items` to replace it, and `<th>` shares this
   * same function. One bad column out of the many numeric columns in this
   * app is a column-author trap, not a `DataTable` bug — fix the cell
   * component itself, the way the examples above already do.
   */
  numeric?: boolean
  /**
   * A supporting column — volume beside a price, a 52-week high beside today's
   * close — set in the muted grey, so the price and the change read first
   * (V2 Market · Movers, `4236:104328`).
   */
  secondary?: boolean
  /**
   * The column a reader scans the table by — the price on Movers, the turnover
   * on All brokers — at 14/600 over every other cell's 12/400. One per table
   * at most: the reference marks its key column this way and leaves the rest
   * of the figures quiet.
   */
  primary?: boolean
  /**
   * A CSS length or percentage for the column, e.g. `'96px'`. A preferred width, not a fixed
   * one: a wider header or figure still widens the column, and a squeezed table may shrink it
   * toward its content before it scrolls.
   */
  width?: string
  /**
   * A width that never GROWS with content — for a Symbol/Company column, whose longest name
   * would otherwise stretch the whole column to fit it, and which truncates with "…" instead.
   * It may shrink: when the figures beside it need the room it gives way, down to `min`, and
   * only then does the table scroll.
   *
   * Its preferred width is `clamp(min, 50cqi, base)`px. `cqi` reads off the table's own box —
   * the same `@container/table` `hideBelow` measures — so `50cqi` reaches `base` once that box is
   * roughly double `base` wide and shrinks the column toward `min` as the table (or a
   * `SectionCard` around it) narrows. `min` is required rather than defaulted: whether a shrunk
   * column still has to fit an icon slot beside its text, or just the text alone, is a per-table
   * call only the caller can make.
   *
   * The table stays on `auto` layout, which sizes every OTHER column from its content, and this
   * one is held by three pieces that each do one job (checked one by one in a real browser
   * against this markup):
   *   - `width: clamp(…)` on the `<th>` and every `<td>` makes it a constrained column: spare
   *     room goes to the unwidthed columns instead of widening this one, and the clamp is what
   *     it sits at whenever everything fits;
   *   - `min-width: min` is the floor a cell's min-content honours. Between the two, a table
   *     too narrow for every column at its preferred width first squeezes any sibling that can
   *     wrap (prose) to its minimum, then shrinks this one toward `min` — figures never wrap,
   *     so in a table of figures this is the column that gives way. Below the floor the table
   *     scrolls;
   *   - the body cell's content sits in a `w-0 min-w-full` box (`FIXED_CELL_CONTENT`), which
   *     adds nothing to either intrinsic size yet fills the cell once laid out. Without it a
   *     long nowrap name still grew the column (200px → 376px); `max-width` or
   *     `overflow: hidden` on the `<td>` itself does not stop that either.
   *
   * This used to switch the WHOLE table to `table-layout: fixed` instead. Fixed layout sizes
   * columns from the header row alone, so every unwidthed column got an equal share of what was
   * left whatever it held: at a ~590px By broker card that was 48px for "Rs 13.43 Cr", which
   * wrapped onto three lines, unbreakable "+4,33,288"s ran into the next column, nowrap headers
   * overlapped, and the scroll box never engaged because the table could not outgrow its box.
   *
   * Mutually exclusive with `width` on the same column — this wins if both are set.
   *
   * REQUIRES at least one sibling column with neither `width` nor `fixedWidth` (a plain,
   * genuinely-unwidthed column) to absorb spare room. Hand every column an explicit width and a
   * table wider than their sum hands the slack to all of them in proportion, this clamp
   * included — 200px became 333px in a 600px table. `table-layout: fixed` does the same (a
   * 9-column table where every column had a width stretched every one of them by the same
   * ratio, confirmed in a real browser), which portfolio's holdings tables rely on on purpose.
   * Every table this ships on leaves at least one plain numeric column unwidthed wherever it
   * can have spare room (CompanyTable's `volume` notes the one rung where it cannot, and why);
   * keep that invariant when adding columns to one.
   */
  fixedWidth?: { base: number; min: number }
  /**
   * The lead column that takes every pixel the others leave (mf:an, the Mutual funds mock's
   * `mf-lead`): give the figure columns beside it a `width` and they hold it, hugging the right
   * edge at every width, while this one grows and truncates. Its body content sits in the same
   * zero-intrinsic box as `fixedWidth`'s, so a long name never widens it; `min` is the floor it
   * shrinks to before the figures give way and, below that, the table scrolls.
   *
   * Not a percentage width: `width: 100%` in auto layout squeezes every sibling to its
   * min-content, `width` or not (measured in Chromium). An unwidthed column with nothing intrinsic
   * in it is the one auto layout hands the spare room to.
   *
   * Mutually exclusive with `width` and `fixedWidth`, and one per table.
   */
  fill?: { min: number }
  /**
   * Drops the column while the TABLE is narrower than this rung.
   *
   * The names read like viewport breakpoints (the first three were, and kept
   * their names so no caller changed), but they are container sizes measured
   * on the table's own box (`@container/table`, see `HIDE_BELOW`).
   *
   *   `'sm'`  — hidden below 384px of table (Tailwind `@sm`, 24rem)
   *   `'md'`  — hidden below 448px of table (`@md`, 28rem)
   *   `'lg'`  — hidden below 576px of table (`@xl`, 36rem)
   *   `'xl'`  — hidden below 672px of table (`@2xl`, 42rem)
   *   `'2xl'` — hidden below 768px of table (`@3xl`, 48rem)
   *   `'3xl'` — hidden below 896px of table (`@4xl`, 56rem)
   *
   * Three rungs, not two, because the column-heavy tables need a middle one:
   * a floorsheet shows symbol, rate and quantity in a phone-width column,
   * gains broker and kitta in a middling one, and only reaches its full eight
   * columns in a wide one. Two rungs force every dropped column to reappear
   * at once. The three above `lg` are for tables whose figures, sized by their
   * content, need more than a desktop track — scrolling is a phone's last
   * resort, never a desktop's default, so a dense table drops its least-asked
   * columns first.
   */
  hideBelow?: keyof typeof HIDE_BELOW
  /** Explains the metric behind a visual column's sort order. */
  sortHint?: string
}

/**
 * One feature set for every Market table.
 *
 * Built once at module scope, as TanStack asks: the object identifies the
 * table's shape, and rebuilding it per render would rebuild the columns with
 * it. Sorting is the only optional feature registered — pagination is the
 * stubs' job (they return one page at a time) and filtering happens in the
 * route's search params, so neither row model would ever have data to work on.
 *
 * `columnMeta` is a type-only slot: TanStack reads the TYPE of what is passed
 * and strips the value, and it is what makes `meta` on a column def typed as
 * DataTableColumnMeta instead of the empty global interface.
 */
const COLUMN_META: DataTableColumnMeta = {}

const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  columnMeta: COLUMN_META,
})

export type DataTableFeatures = typeof dataTableFeatures

/** What a caller builds its `columns` array out of. */
export type DataTableColumn<TRow extends RowData> = ColumnDef<DataTableFeatures, TRow>

const DEFAULT_COLUMN = { enableSorting: true }

/**
 * `hideBelow`'s rungs as CONTAINER queries on the table's own box, not
 * viewport breakpoints.
 *
 * Every table in the app renders inside ScreenLayout's main column, whose
 * width is a function of the viewport, the sidebar, the rail and the Mitra
 * panel. A `lg:table-cell` saw only the viewport, so a 1024 window with the
 * panel open drew all eight floorsheet columns into a column a third of the
 * window wide. Measuring the table itself also covers the tables that sit
 * inside a `SectionCard` (its padding narrower than the track) and any future
 * one in a sheet or a split, with nothing for the caller to pass.
 *
 * The real tracks on the shell (a 240 sidebar, a 260 rail, a 300px Mitra
 * panel up to 1440, 32px gutters): 351 on a 375 phone and 736 on a 768
 * tablet; at 1024, with no rail, 720 (888 with the sidebar collapsed, 388 with
 * Mitra open, 556 with both); at 1280, 684 (852 collapsed, 644 with Mitra
 * open); at 1440, V2's own 844 (1012 collapsed, 804 with Mitra open, 1136 on
 * a screen with no rail). A carded table is 40 narrower. So the rungs land:
 *
 *   `sm`  → `@sm`  384px   hidden on a phone
 *   `md`  → `@md`  448px   also hidden at 1024 with Mitra open
 *   `lg`  → `@xl`  576px   shown from 644 (1280, Mitra open) up; not at 1024
 *                          with Mitra open, sidebar collapsed (556) or not
 *   `xl`  → `@2xl` 672px   shown on every track from 684 up, a tablet's
 *                          included; not at 1280 with Mitra open (644)
 *   `2xl` → `@3xl` 768px   shown at V2's 1440 frame, bare (844) or carded (804)
 *   `3xl` → `@4xl` 896px   only on a wider track: collapsed (1012), no rail (1136)
 *
 * The three from `xl` up came with content-sized columns: a dense table's
 * figures at full width need more than a desktop track, so it drops its
 * least-asked columns there instead of scrolling. A caller picks, for each
 * column, the narrowest rung at which everything visible there still fits
 * with its `fixedWidth` column at `min`, so no width between two rungs
 * scrolls either.
 *
 * Named `/table` so a container a caller puts inside a cell can never capture
 * these queries.
 */
const HIDE_BELOW = {
  sm: 'hidden @sm/table:table-cell',
  md: 'hidden @md/table:table-cell',
  lg: 'hidden @xl/table:table-cell',
  xl: 'hidden @2xl/table:table-cell',
  '2xl': 'hidden @3xl/table:table-cell',
  '3xl': 'hidden @4xl/table:table-cell',
} as const

/**
 * The table panel's side inset (16px at every width, the pages revamp's one table, 2026-10-27),
 * held on the scroll box rather than the outer cells, so the rows and the hairlines between them
 * start and end on the same line as the figures. The head band (`table-head-band`) is the one
 * thing that runs past it, to the panel's edge.
 */
const SIDE_INSET = 'px-4'

/**
 * How far a row's hover wash reaches past its first and last cell: 12px, so the fill stops 4px
 * inside the panel's border rather than meeting it. A `box-shadow` adds no scrollable overflow,
 * so the wash cannot widen the table or the page. `bare` tables have no inset and no wash to
 * spare, so they draw the plain row fill. Read by the `row-wash` utility (globals.css), as is
 * `--wash-fill`, the fill itself.
 */
const WASH_BLEED = '[--wash-bleed:--spacing(3)]'

/**
 * The space between two columns: 8px, 12px once the table reaches `@md/table` (448px), the rung
 * the middle columns arrive on. The outermost cells have none of their own: the inset already
 * holds them off the panel's edge. The last COLUMN is not always the last cell (a link table's
 * touch chevron follows it), so its own end is `LAST_COLUMN_PAD`.
 */
const CELL_PAD = 'px-2 first:pl-0 last:pr-0 @md/table:px-3'
const LAST_COLUMN_PAD = 'pr-0 @md/table:pr-0'

/**
 * The two rows: `md` one line, 44 at every width, and `lg` a name over a
 * subtitle, 56. Each carries its token as well as its class, because the
 * virtualizer estimates a row's height from the token.
 *
 * `cellY` is a body cell's least breathing room, sized so that content made
 * for the row never reaches it — one line in `md`, up to a 48px mark in
 * `lg` — and the row stays exactly on its token. Only content taller than its
 * row outgrows it: a name over a subtitle (37px) in an `md` table becomes a
 * 54px row instead of text sitting on both hairlines.
 */
const ROW_HEIGHTS = {
  /** sz:ca — the stock page's dense one-line rows (mock Price history, `sta-ph` 36px). */
  sm: {
    className: 'h-table-row-compact',
    token: '--table-row-h-compact',
    fallbackRem: 2.25,
    cellY: 'py-1',
  },
  md: { className: 'h-table-row', token: '--table-row-h', fallbackRem: 2.75, cellY: 'py-2' },
  lg: {
    className: 'h-table-row-two-line',
    token: '--table-row-h-two-line',
    fallbackRem: 3.5,
    cellY: 'py-1',
  },
  // sz:pi — a 36 mark between 12 and 12 (Public Issues' `pi-row`), one step over `lg`.
  xl: {
    className: 'h-table-row-mark',
    token: '--table-row-h-mark',
    fallbackRem: 3.75,
    cellY: 'py-1',
  },
} as const

/**
 * The link row's trailing column: a chevron, on a touch screen only. A mouse already has the row's
 * wash and its name's underline to say "this goes somewhere" (the pages revamp draws no chevron); a
 * finger has neither, so the chevron is the affordance there. On a fine pointer the column stays
 * but is zero wide and empty, so the last figure ends on the inset like an unlinked table's, and
 * the cell is still the row's `:last-child` that ends its wash (`row-wash`) even when the last
 * column is `hideBelow`-hidden. Its own column rather than a glyph in the last cell, because a
 * table may `hideBelow` its last column and the chevron has to outlive it. Decorative: the row's
 * first-cell link already names where the row goes.
 */
const CHEVRON_CELL = 'w-0 p-0 pointer-coarse:w-6 pointer-coarse:pl-2'
const CHEVRON = 'hidden size-4 text-muted-foreground pointer-coarse:block'

/**
 * `rowChevron` (br:kit): the Broker Analysis mock draws its chevron on a MOUSE too
 * (`bra-tr::after`), a small tertiary ▸ just past the row's last figure that fades in and slides
 * 4px home as the row washes (the mock's 3px, on the grid). It hangs into the panel's side inset
 * from the zero-width chevron cell, so it costs the columns nothing. A touch screen keeps the plain
 * chevron above.
 */
const ROW_CHEVRON =
  'pointer-events-none absolute top-1/2 left-0 hidden size-3 -translate-x-1 -translate-y-1/2 text-muted-foreground-subtle opacity-0 transition-[opacity,translate] duration-fast pointer-fine:block group-hover/row:translate-x-0 group-hover/row:opacity-100 group-has-focus-visible/row:translate-x-0 group-has-focus-visible/row:opacity-100'

/**
 * The row's hover fill: the table's own (`raised`, the default) or the Broker Analysis tables'
 * inset fill (`washTone="inset"`, the mock's `bra-tr::before`, br:kit). The bleed past the first
 * and last cells reads `--wash-fill`, set beside `WASH_BLEED`.
 *
 * `wash` (sz:kit) is the Signal Zone, stock page and Public Issues row (mock `sz-row::before`):
 * the primary ink at 5% (`--hover-wash`, #1d1d1d on the card), and keyboard focus draws the gold
 * ring round the whole row instead of the wash (`--wash-focus-fill: transparent`, `FOCUS_RING`),
 * as the mock's row link does. The other tones wash on focus as they always have.
 *
 * `soft` (rest:kit) is the Portfolio and Alerts row (mock `pfa-hrow::before`, `al` row, pf2 S3): the
 * raised fill at 60% (`--table-row-hover-soft`, ≈ #161616 on the card), washing on focus as `raised`.
 */
const WASH_TONES = {
  raised: {
    bleed: '[--wash-fill:var(--table-row-hover)]',
    row: 'hover:bg-table-row-hover has-focus-visible:bg-table-row-hover focus-visible:bg-table-row-hover data-card-open:bg-table-row-hover',
  },
  inset: {
    bleed: '[--wash-fill:var(--muted)]',
    row: 'hover:bg-muted has-focus-visible:bg-muted focus-visible:bg-muted data-card-open:bg-muted',
  },
  wash: {
    bleed: '[--wash-fill:var(--hover-wash)] [--wash-focus-fill:transparent]',
    row: 'hover:bg-hover-wash data-card-open:bg-hover-wash',
  },
  soft: {
    bleed: '[--wash-fill:var(--table-row-hover-soft)]',
    row: 'hover:bg-table-row-hover-soft has-focus-visible:bg-table-row-hover-soft focus-visible:bg-table-row-hover-soft data-card-open:bg-table-row-hover-soft',
  },
} as const

/**
 * `wash`'s keyboard focus: the row's gold ring (owner ruling A2: rows keep the gold), 2px, offset 2
 * round the whole row as the mock rings its row link, in place of the first cell link's own ring.
 * The panel's 16px side inset holds it clear of the scroll box's edge.
 */
const FOCUS_RING =
  'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
const FOCUS_RING_LINK = 'rounded-xs focus-visible:outline-none'

/**
 * A link row underlines its name on hover and keyboard focus, as a link does. Only the part a
 * cell marks `data-row-name` (SymbolCell's ticker, say), never the whole first cell: a ticker over
 * its company would otherwise underline both lines.
 */
const ROW_NAME_UNDERLINE =
  '[&_[data-row-name]]:underline-offset-3 [&:has(:focus-visible)_[data-row-name]]:underline [&:hover_[data-row-name]]:underline'

/** `bodySize`: a body cell's own type, under whatever `primary` / `secondary` add. */
const BODY_SIZES = {
  xs: 'text-body-xs',
  m: 'text-body-m',
} as const

const ARIA_SORT = {
  asc: 'ascending',
  desc: 'descending',
} as const

/**
 * Shared by both `rowTo` and `rowLinkOptions` — one real `<a>` per row, in its
 * first cell. Deliberately NO stretched `after:absolute after:inset-0`
 * overlay: WebKit does not make a `relative` `<tr>` a containing block, so the
 * overlays escaped their rows and the last row's link covered the whole card
 * (and the nav around it). The rest of the row navigates through
 * `useRowNavigation` instead.
 */
const ROW_LINK_CLASS =
  'rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const isRight = (meta: DataTableColumnMeta | undefined) =>
  (meta?.align ?? (meta?.numeric === true ? 'right' : 'left')) === 'right'

/**
 * What a column's header and body cells share: alignment, the `hideBelow` rung, and
 * `whitespace-nowrap` on every figure and every right-aligned cell (badges, changes, dates), so a
 * figure's whole width is its column's minimum: "Rs 13.43 Cr" never breaks at its spaces, and
 * the table overflows into its own scroll box before any figure shares a line with its
 * neighbour's. Left-aligned prose (a broker or sector name) still wraps.
 */
const cellClasses = (meta: DataTableColumnMeta | undefined) =>
  cn(
    meta?.align === 'center' ? 'text-center' : isRight(meta) ? 'text-right' : 'text-left',
    meta?.numeric === true || isRight(meta) ? 'whitespace-nowrap' : null,
    meta?.hideBelow === undefined ? null : HIDE_BELOW[meta.hideBelow],
  )

/**
 * How a body cell reads over the `<td>`'s own 12/400: a figure is tabular, a `primary` column
 * steps up to 14/600, and a `secondary` one goes muted. The reference sets most table figures at
 * 12/400 and only the key column at 14/600; a 14/600 on every figure was what made our tables
 * heavy. The header keeps its own label scale, so this is body-only.
 */
const figureClasses = (meta: DataTableColumnMeta | undefined) =>
  cn(
    meta?.numeric === true ? 'tnum' : null,
    meta?.primary === true ? 'text-h-s' : null,
    meta?.secondary === true ? 'text-muted-foreground' : null,
  )

/** See `DataTableColumnMeta.fixedWidth`'s own doc for why `50cqi`. */
const fixedWidthValue = ({ base, min }: { base: number; min: number }): string =>
  `clamp(${String(min)}px, 50cqi, ${String(base)}px)`

/**
 * `<th>` AND `<td>` read this — `fixedWidth` wins over plain `width` when both are set, and sets
 * `min-width` to its `min` as well; see that field's doc for why both.
 */
const columnWidthStyle = (meta: DataTableColumnMeta | undefined): CSSProperties | undefined => {
  if (meta?.fill !== undefined) return { minWidth: `${String(meta.fill.min)}px` }
  if (meta?.fixedWidth !== undefined) {
    return {
      width: fixedWidthValue(meta.fixedWidth),
      minWidth: `${String(meta.fixedWidth.min)}px`,
    }
  }
  return meta?.width === undefined ? undefined : { width: meta.width }
}

/**
 * Holds a `fixedWidth` body cell's content: zero width to the table's column sizing (a cyclic
 * percentage `min-width` counts as 0 there), the cell's full width once laid out — so a long name
 * truncates inside the column instead of widening it.
 */
const FIXED_CELL_CONTENT = 'w-0 min-w-full'

type RowExtraProps = HTMLAttributes<HTMLTableRowElement> & Record<`data-${string}`, string>

interface DataTableProps<TRow extends RowData, TTo extends string | undefined = undefined> {
  /**
   * Keeps its identity across renders: a module-level array, or one memoised on inputs that move
   * only when the member changes something (a period, a side). TanStack renders a function `cell`
   * as a component, so a def rebuilt per render is a new component type, and every cell remounts
   * on every re-render of the table, poll refetches included — replaying its bar's grow and its
   * sparkline's draw. An input that moves with the data, like the largest value a column's bars
   * are scaled to, reaches the cells through a context instead (`IndicesRankTable`,
   * `BrokerHoldingTable`).
   */
  columns: readonly DataTableColumn<TRow>[]
  rows: readonly TRow[]
  getRowId: (row: TRow) => string
  /**
   * Required. A table with no caption is a grid of numbers a screen reader
   * cannot place; sr-only because the page heading above already names it for
   * everyone else.
   */
  caption: string
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  /** For paged data, the route owns sorting and the service sorts before slicing. */
  sortBy?: string | undefined
  sortDesc?: boolean | undefined
  onSortChange?: ((sortBy: string | undefined, sortDesc: boolean) => void) | undefined
  /** Rows arrive already sorted (the usual case — the stubs sort and page). */
  manualSorting?: boolean
  /**
   * Turns the whole row into a link to this route, as a bare path with no
   * dynamic segments — `() => '/brokers/all'`. Cannot express a route that
   * needs `params`; use `rowLinkOptions` for that. Ignored when
   * `rowLinkOptions` is also given.
   */
  rowTo?: (row: TRow) => NonNullable<LinkProps['to']>
  /**
   * Turns the whole row into a link to a parameterised route — TanStack
   * Router's own navigation-options shape, `{ to, params, search?, hash? }` —
   * so `/brokers/$code` can be expressed with its `params` and have them
   * checked the same way a literal `<Link to="/brokers/$code" params={...}>`
   * is checked. `TTo` is inferred from the `to` returned, so a mismatched or
   * missing `params` is a type error here, not a bare-string escape hatch.
   * `undefined` for a row with nowhere to go (an index in a list of stocks):
   * that row stays plain, with no link, wash or chevron.
   *
   * Takes precedence over `rowTo` when both are given.
   */
  rowLinkOptions?: (row: TRow) => LinkOptions<RegisteredRouter, string, TTo> | undefined
  isLoading?: boolean
  /** How many skeleton rows to draw while loading. */
  pageSize?: number
  emptyState?: ReactNode
  /** `md` for one line per row (the default), `lg` for a name over a subtitle, `sm` (sz:ca) for
   *  the stock page's dense 36px rows. */
  rowHeight?: keyof typeof ROW_HEIGHTS
  /**
   * Drops the table's own card — its fill and its side inset — for a table
   * that shares a padded card with other content (a chart, a search box,
   * tabs), which already draws both. The header, rows and hairlines then run
   * the card's content box, so they keep the same inset from its edge.
   */
  bare?: boolean
  className?: string
  /**
   * Extra classes for one row, e.g. to highlight "the broker this page is
   * about" in a table of its peers. Merged with (not a replacement for) the
   * row's own hover/height/border classes, so it composes with `rowTo`'s
   * hover state rather than fighting it.
   */
  rowClassName?: (row: TRow) => string | undefined
  /**
   * Extra attributes and handlers for one row, for a table linked to something beside it (a
   * donut's `useDonutLink` rows). Spread before `rowNavigation`, which wins on a clash.
   */
  rowProps?: (row: TRow) => RowExtraProps
  /**
   * Washes every row on hover, not only the linked ones: the mock's plain trade logs (Floorsheet,
   * Trading Average, Stock Trading) light the row under the pointer though it goes nowhere. A
   * linked row washes either way.
   */
  washRows?: boolean
  /**
   * Off for a link table whose name stays plain on hover (Movers): the wash alone says where the
   * row goes. Default on, the underline under `data-row-name`.
   */
  nameUnderline?: boolean
  /**
   * Link rows also draw the mock's sliding chevron on a mouse (`ROW_CHEVRON`): the Broker Analysis
   * tables. Default off — elsewhere the wash and the name's underline are the affordance. A row
   * card's row (`rowCard`, no link) draws it too and underlines its name, as the mock's Large
   * trades rows do.
   */
  rowChevron?: boolean
  /**
   * The hover fill: see `WASH_TONES`. Default `raised`; `inset` on the Broker Analysis tables;
   * `wash` (the 5% wash, focus = gold ring not wash) on Signal Zone, stock page and Issues tables;
   * `soft` (raised at 60%) on Portfolio, Alerts and the pf ledgers (rest:kit).
   */
  washTone?: keyof typeof WASH_TONES
  /**
   * The head labels' weight. Default `semibold` (the kit's 600); `regular` for the heads the mock
   * sets in 400 (Large trades and its Broker leaders, `brb-card__thead`).
   */
  headWeight?: 'semibold' | 'regular'
  /**
   * The body cells' type. Default `xs`, the tables' 12; `m` sets every cell at 14, as the Mutual
   * funds mock's `mf-num t14` does (mf:an) — `primary` still steps a figure up to 600 and
   * `secondary` still mutes it, so a money column reads 14/400 in the secondary grey.
   */
  bodySize?: keyof typeof BODY_SIZES
  /**
   * A row card for each row (`useRowCard`, the mock's `brb-tip`): shown on hover, shown on
   * keyboard focus (a row that is not a link becomes a focus stop of its own), pinned by a click
   * or a tap. Return undefined for a row with nothing to say. A row that is also a link navigates
   * on click instead of pinning. Rows with a card wash on hover, and keep the wash while it is up.
   */
  rowCard?: (row: TRow) => RowCardContent | undefined
  virtual?: {
    hasNextPage: boolean
    isFetchingNextPage: boolean
    onLoadMore: () => void
  }
}

/**
 * The shared table for market lists and the investment calendar. The order
 * book ladder remains a specialized table because bid/ask rank has meaning.
 *
 * Real `<table>` semantics, not a grid of divs. Row and column count, "column
 * 4 of 8", and the header a cell belongs to all come free from the element —
 * reimplementing that over flexbox is how data tables become unreadable to
 * anyone not looking at them.
 *
 * `rowTo` (or `rowLinkOptions`, for a route with `params`) makes the row a
 * link: one real `<a>` in the leading cell — the keyboard and screen-reader
 * target, with middle click, "open in new tab" and a status bar — plus a
 * router-driven click handler on the `<tr>` for the rest of the row (see
 * `useRowNavigation` for its rules). Wrapping every cell in its own link
 * would announce the same destination eight times per row. It is NOT a
 * stretched-link overlay: that relies on the `<tr>` being a containing block,
 * which WebKit does not honour. `rowClassName` composes with the row's hover
 * state rather than replacing it — both land in the same `cn(...)` call on
 * the `<tr>`.
 *
 * The table panel (the pages revamp's one table, 2026-10-27): a square card with the default
 * hairline round it, a 36px head band of 12px labels in the secondary grey run to the panel's
 * edge, hairlines between rows, and a link row that fills on hover (and on keyboard focus) and
 * underlines its name. A sorted column's head turns foreground. `bare` drops the panel for a
 * table inside a card's own: no border, fill, inset or band, the head on a plain hairline.
 *
 * Sorting is controlled from outside when `sorting` is passed, so a sorted
 * table is a URL. Left uncontrolled the table sorts the rows it was handed,
 * which is what the small unpaginated tables want.
 */
export default function DataTable<
  TRow extends RowData,
  const TTo extends string | undefined = undefined,
>({
  columns,
  rows,
  getRowId,
  caption,
  sorting,
  onSortingChange,
  sortBy,
  sortDesc,
  onSortChange,
  manualSorting = false,
  rowTo,
  rowLinkOptions,
  isLoading = false,
  pageSize = 10,
  emptyState,
  rowHeight = 'md',
  bare = false,
  washRows = false,
  nameUnderline = true,
  rowChevron = false,
  washTone = 'raised',
  headWeight = 'semibold',
  bodySize = 'xs',
  rowCard,
  className,
  rowClassName,
  rowProps,
  virtual,
}: DataTableProps<TRow, TTo>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  // A4: `sortHint` used to land only in a `title`, which a screen reader never
  // announces when the button already has visible text as its accessible
  // name. One id per table instance keeps two DataTables on the same page
  // (or two sortable columns) from writing the same `id` twice.
  const tableId = useId()
  const routeSorting: SortingState =
    sortBy === undefined ? [] : [{ id: sortBy, desc: sortDesc ?? false }]
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data: rows,
    getRowId,
    defaultColumn: DEFAULT_COLUMN,
    manualSorting: manualSorting || onSortChange !== undefined,
    ...(sorting === undefined && onSortChange === undefined
      ? {}
      : { state: { sorting: sorting ?? routeSorting } }),
    ...(onSortChange === undefined
      ? onSortingChange === undefined
        ? {}
        : { onSortingChange }
      : {
          onSortingChange: (updater: SortingState | ((previous: SortingState) => SortingState)) => {
            const next = typeof updater === 'function' ? updater(routeSorting) : updater
            onSortChange(next[0]?.id, next[0]?.desc ?? false)
          },
        }),
  })

  const leafColumns = table.getAllLeafColumns()
  const bodyRows = table.getRowModel().rows
  const virtualizer = useVirtualizer({
    count: virtual === undefined ? 0 : bodyRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => {
      const { token, fallbackRem } = ROW_HEIGHTS[rowHeight]
      if (typeof document === 'undefined') return fallbackRem * 16
      const raw = getComputedStyle(document.documentElement).getPropertyValue(token)
      return (Number(raw.trim().replace(/rem$/, '')) || fallbackRem) * 16
    },
    overscan: 12,
  })
  const virtualItems = virtualizer.getVirtualItems()
  const lastVirtualItem = virtualItems[virtualItems.length - 1]
  const lastVirtualIndex = lastVirtualItem?.index
  const hasNextPage = virtual?.hasNextPage
  const isFetchingNextPage = virtual?.isFetchingNextPage
  const onLoadMore = virtual?.onLoadMore
  useEffect(() => {
    if (lastVirtualIndex === undefined) return
    if (lastVirtualIndex >= bodyRows.length - 6 && hasNextPage && !isFetchingNextPage) {
      onLoadMore?.()
    }
  }, [hasNextPage, isFetchingNextPage, onLoadMore, lastVirtualIndex, bodyRows.length])
  const visibleRows =
    virtual === undefined
      ? bodyRows
      : virtualItems.flatMap((item) => {
          const row = bodyRows[item.index]
          return row === undefined ? [] : [row]
        })
  const topPadding = virtualItems[0]?.start ?? 0
  const bottomPadding = virtualizer.getTotalSize() - (lastVirtualItem?.end ?? 0)
  const rowClasses = cn(
    'border-b border-border-subtle last:border-b-0',
    ROW_HEIGHTS[rowHeight].className,
  )
  const hasRowLink = rowTo !== undefined || rowLinkOptions !== undefined
  // A card row with no link still gets the chevron's column when `rowChevron` asks (br:lh).
  const hasChevronColumn = hasRowLink || (rowChevron && rowCard !== undefined)
  const lastColumnId = leafColumns[leafColumns.length - 1]?.id
  // The chevron's column, when the rows are links, spans with the rest.
  const columnCount = leafColumns.length + (hasChevronColumn ? 1 : 0)
  const rowNavigation = useRowNavigation()
  const card = useRowCard<string>()
  const washRow = cn(WASH_TONES[washTone].row, washTone === 'wash' ? FOCUS_RING : null)
  const rowLinkClass = washTone === 'wash' ? FOCUS_RING_LINK : ROW_LINK_CLASS
  /**
   * What the row's `<a>` points at, as navigate options, or `undefined` for a
   * row that is not a link. `rowLinkOptions(row)` is typed against
   * DataTable's OWN `TTo` type parameter, still abstract inside this generic
   * body, so `<Link>` cannot unify its own (differently-scoped) `TTo` against
   * it, and TS treats it as unsound here even though every concrete call site
   * (a real `TTo` literal, inferred from what the caller returns) type checks
   * the `to`/`params` pair correctly. The public prop stays fully checked;
   * this cast is contained here, not a hole callers can fall into.
   */
  const rowTarget = (row: TRow): NavigateOptions | undefined =>
    rowLinkOptions !== undefined
      ? (rowLinkOptions(row) as unknown as NavigateOptions | undefined)
      : rowTo !== undefined
        ? ({ to: rowTo(row) } as NavigateOptions)
        : undefined

  return (
    <div
      // `data-table-panel`: this box is a panel of its own, so a frame around it can step back
      // (SignalsTableFrame) instead of drawing a second card.
      {...(bare ? {} : { 'data-table-panel': '' })}
      // `@container/table` is what `hideBelow` and the cell padding measure.
      // `w-full` because inline-size containment stops the box sizing to its
      // content: in a shrink-to-fit parent (an `items-start` flex column) it
      // would otherwise collapse to nothing rather than to the table's width.
      className={cn(
        '@container/table w-full overflow-hidden',
        bare ? null : 'border border-border bg-card',
        className,
      )}
    >
      <div
        ref={scrollRef}
        // `relative` makes this scroll box the containing block of every `sr-only` span in the
        // table (the caption, sort hints, `Change`'s direction word). Those are
        // `position: absolute`; with no positioned ancestor they escaped this box's clip and
        // stretched the PAGE sideways whenever the table itself scrolled — 54px at 375.
        // `scroll-fade-x` tints whichever edge still has columns past it (globals.css).
        className={cn(
          'scrollbar-slim scroll-fade-x relative overflow-x-auto overscroll-x-contain',
          bare ? null : [SIDE_INSET, WASH_BLEED, WASH_TONES[washTone].bleed, 'table-head-band'],
          // `wash` rings a focused row 2px outside it (`FOCUS_RING`): 4px under the last row keep
          // that ring's foot inside the scroll box, which clips both ways.
          washTone === 'wash' ? 'pb-1' : null,
          virtual === undefined ? null : 'max-h-128 overflow-y-auto',
        )}
      >
        <table className="w-full border-collapse" aria-busy={isLoading}>
          <caption className="sr-only">{caption}</caption>

          {/* The panel's band is the scroll box's background, which scrolls away under a sticky
              head, so a virtualised head paints its own (the cells' width, not the panel's). */}
          <thead className={virtual === undefined ? undefined : 'sticky top-0 z-10 bg-table-head'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={
                  bare
                    ? 'border-b border-border-subtle'
                    : virtual === undefined
                      ? undefined
                      : 'border-b border-border'
                }
              >
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta
                  const sortable = header.column.getCanSort()
                  const sorted = header.column.getIsSorted()
                  const SortGlyph =
                    sorted === 'asc' ? ChevronUp : sorted === 'desc' ? ChevronDown : ChevronsUpDown
                  // Material data-table rule: on a right-aligned (numeric) column the glyph
                  // leads the label, so the label's right edge lines up with the figures below
                  // it; a left-aligned column keeps the glyph trailing (see `isRight` below).
                  const sortGlyph = (
                    <SortGlyph
                      aria-hidden="true"
                      className={cn(
                        'size-3 shrink-0',
                        // Only the sorted column prints its glyph; the others show a faint one
                        // while their header is hovered or focused. Hidden, not removed, so the
                        // label never shifts when it arrives.
                        sorted === false
                          ? 'opacity-0 transition-opacity duration-fast group-hover/sort:opacity-60 group-focus-visible/sort:opacity-60 pointer-coarse:opacity-60'
                          : 'text-foreground',
                      )}
                    />
                  )

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      colSpan={header.colSpan}
                      aria-sort={
                        sortable ? (sorted === false ? 'none' : ARIA_SORT[sorted]) : undefined
                      }
                      style={columnWidthStyle(meta)}
                      className={cn(
                        'h-table-head text-column-head whitespace-nowrap transition-colors',
                        headWeight === 'regular' && 'font-normal',
                        sorted === false ? 'text-muted-foreground-subtle' : 'text-foreground',
                        CELL_PAD,
                        header.column.id === lastColumnId ? LAST_COLUMN_PAD : null,
                        cellClasses(meta),
                      )}
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          title={meta?.sortHint}
                          aria-describedby={
                            meta?.sortHint === undefined
                              ? undefined
                              : `${tableId}-${header.id}-hint`
                          }
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            // A 12px label's own line box is under the WCAG 2.5.8 24px floor and
                            // under 44 on touch, though the `<th>` around it is 36. `h-full`
                            // reaches the header's own height (`h-table-head` has no vertical
                            // padding to fight); `TOUCH_TARGET` then grows a `::before` past that
                            // below `lg`.
                            // The mock's sort head: an idle label steps from the tertiary ink to
                            // the secondary on hover; the sorted one is already the primary.
                            'group/sort relative inline-flex h-full cursor-pointer items-center gap-1 rounded-xs transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                            sorted === false
                              ? 'hover:text-muted-foreground focus-visible:text-muted-foreground'
                              : null,
                            TOUCH_TARGET,
                          )}
                        >
                          {isRight(meta) ? (
                            <>
                              {sortGlyph}
                              <table.FlexRender header={header} />
                            </>
                          ) : (
                            <>
                              <table.FlexRender header={header} />
                              {sortGlyph}
                            </>
                          )}
                          {meta?.sortHint === undefined ? null : (
                            <span id={`${tableId}-${header.id}-hint`} className="sr-only">
                              {meta.sortHint}
                            </span>
                          )}
                        </button>
                      ) : meta?.sortHint === undefined ? (
                        <table.FlexRender header={header} />
                      ) : (
                        // A4, the deeper half of it: both `BusyNamesTable` and
                        // `TradesTable` attach `sortHint` to a column with
                        // `enableSorting: false` — the `sortable` branch above
                        // never ran for them, so the hint was not merely
                        // title-only, it rendered NOTHING, sortable or not.
                        // `tabIndex` makes the explanation keyboard-reachable
                        // the same way the sortable case's button already was.
                        <span
                          tabIndex={0}
                          title={meta.sortHint}
                          aria-describedby={`${tableId}-${header.id}-hint`}
                          className="rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        >
                          <table.FlexRender header={header} />
                          <span id={`${tableId}-${header.id}-hint`} className="sr-only">
                            {meta.sortHint}
                          </span>
                        </span>
                      )}
                    </th>
                  )
                })}
                {/* A `<td>`, not an empty `<th>`: it names no column. */}
                {hasChevronColumn ? <td aria-hidden="true" className={CHEVRON_CELL} /> : null}
              </tr>
            ))}
          </thead>

          {/* The rows stagger in once, when the first data lands; skeleton rows do not. Once
              played the body is marked `data-entered`, so a sort, a page change, a poll or a
              virtualised scroll never replays it, and the mark survives a later reload. The
              same mark ends the marks inside the cells (globals.css, TABLE MARKS), since a sort
              moves a row's nodes and a moved node restarts its animations, so it waits for
              those entrances too (`subtree`): it lands once the last sparkline has drawn. */}
          <Stagger as="tbody" active={!isLoading} subtree>
            {isLoading ? (
              Array.from({ length: pageSize }, (_, index) => (
                // One bar a row, not one a cell: the row keeps its loaded height and the header
                // above still holds the column widths, so nothing moves when the rows land.
                <tr key={index} className={rowClasses}>
                  <td colSpan={columnCount} className="px-0">
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : bodyRows.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="p-0">
                  {emptyState ?? <EmptyState title="Nothing to show" />}
                </td>
              </tr>
            ) : (
              <>
                {virtual !== undefined && topPadding > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={columnCount} style={{ height: topPadding, padding: 0 }} />
                  </tr>
                ) : null}
                {visibleRows.map((row) => {
                  const target = rowTarget(row.original)
                  const cardContent = rowCard?.(row.original)
                  return (
                    <tr
                      key={row.id}
                      {...rowProps?.(row.original)}
                      {...(cardContent === undefined
                        ? {}
                        : {
                            ...card.bind(row.id, () => cardContent),
                            // A card row with no link is its own focus stop, so a keyboard reads it.
                            ...(target === undefined ? { tabIndex: 0 } : {}),
                          })}
                      {...rowNavigation(target)}
                      className={cn(
                        rowClasses,
                        // Every hover is also a keyboard focus (V2's hover model):
                        // tabbing onto the row's link lights the row it stands for.
                        target === undefined
                          ? washRows || cardContent !== undefined
                            ? [
                                'row-wash transition-colors',
                                washRow,
                                cardContent === undefined
                                  ? null
                                  : [
                                      'cursor-pointer focus-visible:outline-none',
                                      rowChevron ? 'group/row' : null,
                                      nameUnderline ? ROW_NAME_UNDERLINE : null,
                                    ],
                              ]
                            : null
                          : [
                              'row-wash group/row cursor-pointer transition-colors',
                              washRow,
                              nameUnderline ? ROW_NAME_UNDERLINE : null,
                            ],
                        rowClassName?.(row.original),
                      )}
                    >
                      {row.getAllCells().map((cell, index) => {
                        const meta = cell.column.columnDef.meta
                        const content = <table.FlexRender cell={cell} />
                        const cellTarget = index === 0 ? target : undefined
                        const linked =
                          cellTarget === undefined ? (
                            content
                          ) : (
                            <Link {...cellTarget} className={rowLinkClass}>
                              {content}
                            </Link>
                          )

                        const lastColumn = cell.column.id === lastColumnId

                        return (
                          <td
                            key={cell.id}
                            // The row's last figure ends the wash when a zero-wide chevron cell
                            // follows it: that cell's shadow has no box to throw (`row-wash`).
                            data-wash-end={lastColumn && hasChevronColumn ? '' : undefined}
                            style={columnWidthStyle(meta)}
                            className={cn(
                              BODY_SIZES[bodySize],
                              ROW_HEIGHTS[rowHeight].cellY,
                              CELL_PAD,
                              lastColumn ? LAST_COLUMN_PAD : null,
                              cellClasses(meta),
                              figureClasses(meta),
                            )}
                          >
                            {meta?.fixedWidth === undefined && meta?.fill === undefined ? (
                              linked
                            ) : (
                              <div className={FIXED_CELL_CONTENT}>{linked}</div>
                            )}
                          </td>
                        )
                      })}
                      {hasChevronColumn ? (
                        <td
                          aria-hidden="true"
                          className={cn(CHEVRON_CELL, rowChevron && 'relative')}
                        >
                          {target === undefined ? (
                            rowChevron && cardContent !== undefined ? (
                              <ChevronRight strokeWidth={2.5} className={ROW_CHEVRON} />
                            ) : null
                          ) : (
                            <>
                              <ChevronRight className={CHEVRON} />
                              {rowChevron ? (
                                <ChevronRight strokeWidth={2.5} className={ROW_CHEVRON} />
                              ) : null}
                            </>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  )
                })}
                {virtual !== undefined && bottomPadding > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={columnCount} style={{ height: bottomPadding, padding: 0 }} />
                  </tr>
                ) : null}
              </>
            )}
          </Stagger>
        </table>
        {rowCard === undefined ? null : <RowCard card={card} />}
        {virtual?.isFetchingNextPage ? (
          <div className="p-3 text-center text-body-xs text-muted-foreground">Loading more…</div>
        ) : null}
      </div>
    </div>
  )
}
